import React from 'react'
import { Music, ExternalLink, Loader2 } from 'lucide-react'
import { Button } from '~/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/ui/card'
import { Badge } from '~/ui/badge'

interface SpotifyConnectionCardProps {
  isConnected: boolean
  spotifyId?: string | null
  onConnect: () => void
  onDisconnect: () => void
  isLoading?: boolean
}

export const SpotifyConnectionCard: React.FC<SpotifyConnectionCardProps> = ({
  isConnected,
  spotifyId,
  onConnect,
  onDisconnect,
  isLoading = false,
}) => {
  return (
    <Card className="w-full max-w-md">
      <CardHeader className="flex flex-row items-center space-y-0 pb-4">
        <div className="flex items-center space-x-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100">
            <Music className="h-6 w-6 text-green-600" />
          </div>
          <div>
            <CardTitle className="text-lg">Spotify</CardTitle>
            <CardDescription className="text-sm">
              Connect your Spotify account to import playlists
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium">Status:</span>
            {isConnected ? (
              <Badge variant="default" className="bg-green-100 text-green-800">
                Connected
              </Badge>
            ) : (
              <Badge variant="secondary">Not Connected</Badge>
            )}
          </div>
        </div>

        {isConnected && spotifyId && (
          <div className="bg-muted rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Spotify Account</p>
                <p className="text-muted-foreground text-xs">{spotifyId}</p>
              </div>
              <ExternalLink className="text-muted-foreground h-4 w-4" />
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-2">
          {isConnected ? (
            <Button
              variant="outline"
              size="sm"
              onClick={onDisconnect}
              disabled={isLoading}
              className="text-destructive hover:text-destructive"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Disconnecting...
                </>
              ) : (
                'Disconnect'
              )}
            </Button>
          ) : (
            <Button
              variant="default"
              size="sm"
              onClick={onConnect}
              disabled={isLoading}
              className="bg-green-600 hover:bg-green-700"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <Music className="mr-2 h-4 w-4" />
                  Connect Spotify
                </>
              )}
            </Button>
          )}
        </div>

        {!isConnected && (
          <div className="text-muted-foreground text-xs">
            <p>Connecting your Spotify account allows you to:</p>
            <ul className="mt-1 ml-4 list-disc space-y-1">
              <li>Import your existing playlists</li>
              <li>View track metadata from Spotify</li>
              <li>Manage your music library</li>
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
